import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Forum } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HomeIcon from '@mui/icons-material/Home';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { Badge } from '@mui/material';
import { Button, Divider, Drawer, Link } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  FeatureSubscriptionType,
  PortalPermissions,
  useEstateTaskInstance_HasUpcomingDeadlineQuery,
} from 'queries';

import CreateEstateModal from 'pages/estates/components/create-estate-modal';
import { canAddOrEditClientMatter } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import useHasPortalPermission from 'utils/useHasPortalPermission';

import SubNavBar from './sub-nav-bar';

export const LEFT_NAV_BAR_WIDTH = 260;

const useStyles = makeStyles((theme) => ({
  drawer: {
    marginTop: 64,
    width: LEFT_NAV_BAR_WIDTH,
    backgroundColor: '#DCF4FE',
    color: '#1C2541',
  },
  increasedMargin: {
    marginTop: 104,
  },
  drawerContent: {
    padding: theme.spacing(0, 1),
    fontSize: '14px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  navItem: {
    display: 'flex',
    cursor: 'pointer',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    color: '#1C2541',
    '&:hover': {
      backgroundColor: '#D351E1',
      color: '#FFF',
      borderRadius: 8,
    },
  },
  navItemTitle: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
  },
  label: {
    marginLeft: theme.spacing(1),
    color: 'inherit',
  },
  addClientButton: {
    backgroundColor: '#5ED1F0',
    width: '100%',
    fontSize: '14px',
    color: '#1C2541',
    padding: '10px 20px',
    borderRadius: '100px',
    margin: theme.spacing(2, 0),
  },
  deadlineTrackerText: {
    display: 'flex',
    alignItems: 'center',
  },
  notificationDot: {
    marginLeft: theme.spacing(8),
    '& .MuiBadge-dot': {
      marginLeft: 5,
      alignSelf: 'center',
      width: 14,
      height: 14,
      borderRadius: '50%',
    },
  },
}));

type Props = {
  isOpen: boolean;
  featureSubscriptionType: FeatureSubscriptionType;
  isInviteTeamMemberBannerVisible: boolean;
};

type NavItem = {
  id: string;
  label: React.ReactNode;
  icon?: JSX.Element;
  path?: string;
  subNav?: JSX.Element;
  isExternal?: boolean;
};

type SubNavType = 'estates' | 'vaults';

const LeftNavBar = ({
  isOpen,
  featureSubscriptionType,
  isInviteTeamMemberBannerVisible,
}: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);
  const hasPortalPermissionUser = useHasPortalPermission(PortalPermissions.User);

  const [expandedSubNav, setExpandedSubNav] = useState<SubNavType | null>(null);
  const [showCreateEstateModal, setShowCreateEstateModal] = useState(false);

  const handleToggleExpanded = (type: SubNavType) => {
    if (expandedSubNav === type) {
      setExpandedSubNav(null);
    } else {
      setExpandedSubNav(type);
    }
  };

  const { data: hasUpcomingDeadline } = useEstateTaskInstance_HasUpcomingDeadlineQuery({
    variables: {
      input: {
        daysAhead: 3,
        portalId,
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting deadlines: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  let navItems: NavItem[] = [
    {
      id: 'Home',
      label: 'Home',
      icon: <HomeIcon fontSize="small" />,
      path: `/home/${portalId}/home`,
    },
  ];

  if (hasPortalPermissionAdmin || hasPortalPermissionUser) {
    navItems = [
      ...navItems,
      {
        id: 'Estates',
        label: 'Estates',
        subNav: (
          <SubNavBar
            type="estates"
            isExpanded={expandedSubNav === 'estates'}
            toggleExpanded={() => handleToggleExpanded('estates')}
          />
        ),
      },
      {
        id: 'Vaults',
        label: 'Vaults',
        subNav: (
          <SubNavBar
            type="vaults"
            isExpanded={expandedSubNav === 'vaults'}
            toggleExpanded={() => handleToggleExpanded('vaults')}
          />
        ),
      },
      {
        id: 'Deadline Tracker',
        icon: <TimerOutlinedIcon fontSize="small" />,
        label: (
          <span className={classes.deadlineTrackerText}>
            Deadline Tracker
            {hasUpcomingDeadline?.data && (
              <Badge color="error" variant="dot" className={classes.notificationDot} />
            )}
          </span>
        ),
        path: `/home/${portalId}/deadline-tracker`,
      },
      {
        id: 'Assigned Tasks',
        label: 'Assigned Tasks',
        icon: <PlaylistAddCheckIcon fontSize="small" />,
        path: `/home/${portalId}/assigned-tasks`,
      },
      {
        id: 'Universal Will/Trust Provisons',
        label: 'Universal Will/Trust Provisons',
        icon: <AttachFileIcon fontSize="small" />,
        path: 'https://trustate.com/help-center/universal-will-trust-provisions',
        isExternal: true,
      },
      {
        id: 'CLE Courses',
        label: 'CLE Courses',
        icon: <LocalLibraryIcon fontSize="small" />,
        path: 'https://courses.sprouteducation.com/Discount/trustate-741078',
        isExternal: true,
      },
      {
        id: 'Resources & Help Center',
        label: 'Resources & Help Center',
        icon: <LibraryBooksIcon fontSize="small" />,
        path: 'https://trustate.com/help-center',
        isExternal: true,
      },
      {
        id: 'Trustate Community',
        label: 'Trustate Community',
        icon: <Forum fontSize="small" />,
        path: 'https://community.trustate.com/join?invitation_token=c45289b5655683d665a58b458502de9f0bcddbf1-c6e485e3-9487-4fa0-be84-72a4d70c44af',
        isExternal: true,
      },
    ];
  }

  const drawerClassName = clsx(classes.drawer, {
    [classes.increasedMargin]: isInviteTeamMemberBannerVisible,
  });

  return (
    <>
      <Drawer
        className={drawerClassName}
        variant="persistent"
        anchor="left"
        open={isOpen}
        classes={{ paper: drawerClassName }}
      >
        <Divider />
        <div className={classes.drawerContent}>
          {(hasPortalPermissionAdmin || hasPortalPermissionUser) && (
            <Button
              variant="contained"
              type="button"
              className={classes.addClientButton}
              onClick={() => {
                if (!canAddOrEditClientMatter(featureSubscriptionType)) {
                  showToast({
                    message: 'Please upgrade your account to add a new client/matter.',
                    severity: NotificationSeverity.WARNING,
                  });
                  return;
                }

                setShowCreateEstateModal(true);
              }}
            >
              <AddIcon /> Add Client/Matter
            </Button>
          )}
          <div>
            {navItems.map((navItem) =>
              navItem.subNav ? (
                <div key={navItem.id}>{navItem.subNav}</div>
              ) : (
                <Link
                  underline="none"
                  key={navItem.id}
                  className={classes.navItem}
                  href={navItem.path}
                  target={navItem.isExternal ? '_blank' : '_self'}
                >
                  <div className={classes.navItemTitle}>
                    {navItem.icon}
                    <div className={classes.label}>{navItem.label}</div>
                  </div>
                </Link>
              ),
            )}
          </div>
          {/* commenting out until we have a use case for this */}
          {/* {showInviteTeamMembersCard && (
            <div
              style={{
                position: 'absolute',
                bottom: 80,
              }}
            >
              <InviteTeamMembers onClose={() => setShowInviteTeamMembersCard(false)} />
            </div>
          )} */}
        </div>
      </Drawer>
      {(hasPortalPermissionAdmin || hasPortalPermissionUser) && (
        <CreateEstateModal
          showModal={showCreateEstateModal}
          setShowModal={setShowCreateEstateModal}
        />
      )}
    </>
  );
};

export default LeftNavBar;
